import { timeZoneMapping } from './enum';

/**
 * Static lookup: IANA timezone → abbreviation label.
 * Built by reversing the timeZoneMapping (abbreviation → IANA)
 * and grouping abbreviations per IANA zone.
 *
 * Zones with DST show both: "CET/CEST"
 * Zones without DST show one: "JST"
 */
const timeZoneAbbreviations: Record<string, string> = buildAbbreviationMap();

function buildAbbreviationMap(): Record<string, string> {
  const ianaToAbbrevs: Record<string, Set<string>> = {};

  for (const [abbrev, iana] of Object.entries(timeZoneMapping)) {
    if (iana === 'UTC') continue;
    if (!ianaToAbbrevs[iana]) {
      ianaToAbbrevs[iana] = new Set();
    }
    ianaToAbbrevs[iana].add(abbrev);
  }

  const result: Record<string, string> = {};

  for (const [iana, abbrevs] of Object.entries(ianaToAbbrevs)) {
    const sorted = [...abbrevs].sort((a, b) => a.length - b.length);

    // Pick up to 2 distinct abbreviations (standard + DST).
    // Prefer shorter abbreviation first (usually standard time).
    const unique = sorted.length <= 2 ? sorted : sorted.slice(0, 2);
    result[iana] = unique.join('/');
  }

  return result;
}

/**
 * Formats a timezone IANA identifier for display in the UI.
 *
 * Examples:
 *   "Europe/Berlin"  → "Europe/Berlin (CET/CEST)"
 *   "Asia/Tokyo"     → "Asia/Tokyo (JST)"
 *   "UTC"            → "UTC"
 *   "America/Denver" → "America/Denver (MST/MDT)"
 */
export function formatTimeZoneLabel(tz: string): string {
  if (tz === 'UTC') return 'UTC';

  const abbrev = timeZoneAbbreviations[tz];
  if (abbrev) {
    return `${tz} (${abbrev})`;
  }

  return tz;
}
